/* SPRAY di SOLÉ — theme.js
   Handles: sticky header, mobile drawer, cart drawer, AJAX add-to-cart,
   product gallery, variant selection, reveal animations, search.
*/

(function () {
  'use strict';

  const routes = (window.theme && window.theme.routes) || {
    cart_url: '/cart',
    cart_add_url: '/cart/add',
    cart_change_url: '/cart/change',
    cart_update_url: '/cart/update',
    predictive_search_url: '/search/suggest'
  };

  const moneyFormat = (window.theme && window.theme.moneyFormat) || '${{amount}}';

  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10);
    if (isNaN(cents)) return '';
    const value = (cents / 100).toFixed(2);
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(cents / 100));
  }

  /* ---------- Reveal on scroll ---------- */
  function setupReveal() {
    const items = document.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window) || items.length === 0) {
      items.forEach(el => el.classList.add('is-visible'));
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    items.forEach(el => obs.observe(el));
  }

  /* ---------- Sticky / transparent header ---------- */
  function setupHeader() {
    const header = document.querySelector('[data-site-header]');
    if (!header) return;
    const headerMain = header.querySelector('[data-header-main]');
    const isTransparent = header.dataset.transparent === 'true';
    if (!isTransparent) return;

    function update() {
      if (window.scrollY > 60) {
        headerMain.classList.remove('transparent');
      } else {
        headerMain.classList.add('transparent');
      }
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ---------- Drawer helpers ---------- */
  function openDrawer(el) {
    if (!el) return;
    el.classList.add('open');
    el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer(el) {
    if (!el) return;
    el.classList.remove('open');
    el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  /* ---------- Mobile menu ---------- */
  function setupMobileMenu() {
    const drawer = document.querySelector('[data-mobile-drawer]');
    if (!drawer) return;
    document.querySelectorAll('[data-mobile-toggle]').forEach(btn => {
      btn.addEventListener('click', () => openDrawer(drawer));
    });
    drawer.querySelectorAll('[data-mobile-close]').forEach(btn => {
      btn.addEventListener('click', () => closeDrawer(drawer));
    });
  }

  /* ---------- Search ---------- */
  function setupSearch() {
    const modal = document.querySelector('[data-search-modal]');
    if (!modal) return;
    document.querySelectorAll('[data-search-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openDrawer(modal);
        const input = modal.querySelector('input[type="search"]');
        if (input) setTimeout(() => input.focus(), 200);
      });
    });
    modal.querySelectorAll('[data-search-close]').forEach(btn => {
      btn.addEventListener('click', () => closeDrawer(modal));
    });

    /* Predictive search */
    const form = modal.querySelector('[data-search-form]');
    const results = modal.querySelector('[data-search-results]');
    if (form && results) {
      const input = form.querySelector('input[type="search"]');
      let t;
      input.addEventListener('input', () => {
        clearTimeout(t);
        const q = input.value.trim();
        if (!q || q.length < 2) { results.innerHTML = ''; return; }
        t = setTimeout(() => {
          fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`)
            .then(r => r.text())
            .then(html => {
              const items = extractPredictive(html, q);
              results.innerHTML = items.length
                ? items.map(p => `<a href="${p.url}" style="display:flex;gap:1rem;padding:.85rem 0;border-bottom:1px solid rgba(212,175,55,.25)">${p.image ? `<img src="${p.image}" style="width:60px;height:60px;object-fit:cover;background:var(--peach)">` : ''}<div><div class="font-serif" style="font-size:1.05rem">${p.title}</div><div style="font-size:.85rem;color:var(--gold-dark);margin-top:.25rem">${p.price}</div></div></a>`).join('')
                : `<p class="text-sm text-muted">No results for &ldquo;${q}&rdquo;.</p>`;
            })
            .catch(() => {});
        }, 240);
      });
    }
  }

  function extractPredictive(html, q) {
    const wrap = document.createElement('div');
    wrap.innerHTML = html;
    const cards = wrap.querySelectorAll('a[href*="/products/"]');
    const seen = new Set();
    const items = [];
    cards.forEach(a => {
      const url = a.getAttribute('href');
      if (!url || seen.has(url)) return;
      seen.add(url);
      const img = a.querySelector('img');
      const titleEl = a.querySelector('h3, .product-card__title, [class*="title"]');
      const priceEl = a.querySelector('[class*="price"]');
      items.push({
        url,
        image: img ? img.getAttribute('src') : '',
        title: (titleEl ? titleEl.textContent : a.textContent).trim().slice(0, 80),
        price: priceEl ? priceEl.textContent.trim() : ''
      });
    });
    return items.slice(0, 6);
  }

  /* ---------- Cart drawer ---------- */
  const cartState = { items: 0, total: 0 };

  function setupCart() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        if (window.theme && window.theme.cartType === 'page') return;
        e.preventDefault();
        openCart();
      });
    });
    drawer.querySelectorAll('[data-cart-close]').forEach(btn => {
      btn.addEventListener('click', () => closeDrawer(drawer));
    });

    /* Quantity / remove inside drawer */
    drawer.addEventListener('click', (e) => {
      const line = e.target.closest('[data-cart-line]');
      if (!line) return;

      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        const key = removeBtn.dataset.lineKey;
        updateLine(key, 0);
        return;
      }

      const qtyBtn = e.target.closest('[data-cart-qty]');
      if (qtyBtn) {
        const input = line.querySelector('[data-cart-qty-input]');
        if (!input) return;
        const delta = parseInt(qtyBtn.dataset.cartQty, 10);
        const next = Math.max(0, parseInt(input.value, 10) + delta);
        updateLine(input.dataset.lineKey, next);
      }
    });

    drawer.addEventListener('change', (e) => {
      if (e.target.matches('[data-cart-qty-input]')) {
        const next = Math.max(0, parseInt(e.target.value, 10) || 0);
        updateLine(e.target.dataset.lineKey, next);
      }
    });
  }

  function openCart() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    openDrawer(drawer);
    refreshCart();
  }

  function refreshCart() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    return fetch(routes.cart_url + '?section_id=main-cart-drawer-contents', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(r => r.ok ? r.text() : null)
      .then(html => {
        // If section endpoint not available, fall back to cart.js
        if (!html) return reloadFromJSON();
        const wrap = document.createElement('div');
        wrap.innerHTML = html;
        const items = wrap.querySelector('[data-cart-items]');
        if (items) drawer.querySelector('[data-cart-items]').innerHTML = items.innerHTML;
      })
      .catch(reloadFromJSON);
  }

  function reloadFromJSON() {
    return fetch(routes.cart_url + '.js')
      .then(r => r.json())
      .then(cart => {
        cartState.items = cart.item_count;
        cartState.total = cart.total_price;
        updateCartCount(cart.item_count);
        updateCartTotal(cart.total_price);
        const drawer = document.querySelector('[data-cart-drawer]');
        if (!drawer) return;
        const itemsEl = drawer.querySelector('[data-cart-items]');
        const footEl = drawer.querySelector('[data-cart-foot]');
        if (cart.item_count === 0) {
          itemsEl.innerHTML = '<div class="cart-empty"><p class="font-serif italic text-xl" style="margin-bottom:1.5rem">Your bag is empty.</p><a href="/collections/all" class="btn-gold">Discover the collection</a></div>';
          if (footEl) footEl.setAttribute('hidden', '');
        } else {
          if (footEl) footEl.removeAttribute('hidden');
          itemsEl.innerHTML = cart.items.map(renderLine).join('');
        }
      });
  }

  function renderLine(item) {
    return `<div class="cart-line" data-cart-line data-key="${item.key}">
      <a href="${item.url}">${item.image ? `<img src="${item.image}" alt="${escapeHtml(item.product_title)}" class="cart-line__img" loading="lazy">` : ''}</a>
      <div>
        <a href="${item.url}" class="cart-line__title">${escapeHtml(item.product_title)}</a>
        ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-line__variant">${escapeHtml(item.variant_title)}</p>` : ''}
        <p class="cart-line__variant" style="color:var(--gold-dark);margin-top:.35rem">${formatMoney(item.final_price)}</p>
        <div class="cart-line__qty">
          <button type="button" data-cart-qty="-1" aria-label="Decrease">&minus;</button>
          <input type="number" min="0" value="${item.quantity}" data-cart-qty-input data-line-key="${item.key}">
          <button type="button" data-cart-qty="1" aria-label="Increase">&plus;</button>
        </div>
        <button type="button" class="cart-line__remove" data-cart-remove data-line-key="${item.key}">REMOVE</button>
      </div>
      <div style="text-align:right"><span class="font-serif text-base">${formatMoney(item.final_line_price)}</span></div>
    </div>`;
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  function updateCartTotal(total) {
    document.querySelectorAll('[data-cart-total]').forEach(el => {
      el.textContent = formatMoney(total);
    });
  }

  function updateLine(key, quantity) {
    return fetch(routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    }).then(r => r.json()).then(() => reloadFromJSON());
  }

  function addToCart(formOrPayload) {
    let body;
    let isForm = formOrPayload instanceof HTMLFormElement;
    if (isForm) {
      body = new FormData(formOrPayload);
    } else {
      body = JSON.stringify(formOrPayload);
    }
    return fetch(routes.cart_add_url + '.js', {
      method: 'POST',
      headers: isForm ? { 'Accept': 'application/json' } : { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body
    })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(() => reloadFromJSON())
      .then(() => { openCart(); showToast('Added to bag.'); });
  }

  function setupAddToCart() {
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-product-form]');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('[data-add-button]');
      if (btn) btn.disabled = true;
      addToCart(form).catch((res) => {
        return res.json().then(err => showToast(err.description || 'Could not add. Try again.'));
      }).finally(() => { if (btn) btn.disabled = false; });
    });

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();
      const id = btn.dataset.variantId;
      if (!id) return;
      btn.disabled = true;
      addToCart({ items: [{ id: parseInt(id, 10), quantity: 1 }] })
        .catch(() => showToast('Could not add. Try again.'))
        .finally(() => { btn.disabled = false; });
    });
  }

  /* ---------- Quantity widgets (PDP, cart) ---------- */
  function setupQty() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty]');
      if (!btn) return;
      const input = btn.parentElement.querySelector('[data-qty-input]');
      if (!input) return;
      const delta = parseInt(btn.dataset.qty, 10);
      const next = Math.max(parseInt(input.min || '1', 10), parseInt(input.value, 10) + delta);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- Product gallery + variant ---------- */
  function setupProduct() {
    const gallery = document.querySelector('[data-product-gallery]');
    if (gallery) {
      const main = gallery.querySelector('[data-gallery-main] img');
      gallery.querySelectorAll('[data-thumb]').forEach(thumb => {
        thumb.addEventListener('click', () => {
          gallery.querySelectorAll('[data-thumb]').forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
          if (main) main.src = thumb.dataset.full;
        });
      });
    }

    const select = document.querySelector('[data-variant-select]');
    if (!select) return;

    document.querySelectorAll('[data-option-input]').forEach(input => {
      input.addEventListener('change', () => {
        const form = input.closest('form');
        const optionInputs = form.querySelectorAll('[data-option-input]:checked');
        const chosen = Array.from(optionInputs).map(i => i.value);
        const opt = Array.from(select.options).find(o => o.dataset.options === chosen.join(' / '));
        if (!opt) return;
        select.value = opt.value;
        const priceEl = document.querySelector('[data-product-price]');
        if (priceEl) {
          let html = opt.dataset.price;
          if (opt.dataset.compare) html += ' <s>' + opt.dataset.compare + '</s>';
          priceEl.innerHTML = html;
        }
        const addBtn = form.querySelector('[data-add-button]');
        const addText = form.querySelector('[data-add-text]');
        if (opt.disabled) {
          if (addBtn) addBtn.disabled = true;
          if (addText) addText.textContent = 'Sold Out';
        } else {
          if (addBtn) addBtn.disabled = false;
          if (addText) addText.textContent = 'Add to Bag';
        }
      });
    });
  }

  /* ---------- Toast ---------- */
  let toastTimer;
  function showToast(message) {
    const target = document.getElementById('ToastTarget') || document.body;
    let toast = target.querySelector('.toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'toast';
      target.appendChild(toast);
    }
    toast.textContent = message;
    requestAnimationFrame(() => toast.classList.add('show'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('show'), 2400);
  }

  /* ---------- Utilities ---------- */
  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /* ---------- ESC key closes drawers ---------- */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('.cart-drawer.open, .mobile-drawer.open, .search-modal.open').forEach(el => closeDrawer(el));
  });

  /* ---------- Init ---------- */
  function init() {
    setupReveal();
    setupHeader();
    setupMobileMenu();
    setupSearch();
    setupCart();
    setupAddToCart();
    setupQty();
    setupProduct();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
